import Chart from 'chart.js/auto';
import { datalabelFont, legendConfig, legendPad, onEthnicityResize, onResize, tooltipTitleFont } from './chartJsPlugins';
import { Context } from 'chartjs-plugin-datalabels';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { ethnicityTopLabels, topLabels, topLabelsTrends, htmlLegendPlugin, generateChartId } from './configs';
import { infoBubble } from "../../helpers/infoBubble";
import { fillMissingData, StandardMetricSampleRow, upperizeStringify } from './chartUtils';


// @ts-ignore
window.initMetricBlock = (function ($, blockId, config, schoolResponse, networkResponse, districtResponse, bubbleConfigs, notUseDefaultTooltip) {
    const chartsConfig = {
        trends: {
            plugins: [ChartDataLabels, topLabels, htmlLegendPlugin],

        },
        snapshots: {
            plugins: [ChartDataLabels, topLabels, htmlLegendPlugin]
        },
    }

    const getTooltip = (key) => {
        return bubbleConfigs[key] ?? '';
    }
    const getCategoryMappedTooltip = (category) => {
        const mappedField = mappedBubbleConfigFieldKeys[category];
        return getTooltip(mappedField);
    }
    // @TODO create new keys for these
    const mappedBubbleConfigFieldKeys =
    {
        "ALL": "allstudents",
        "Economic Disadvantage": "economicdisadvantage",
        "English Learner Status": "englishlearner",
        "Gender": "gender",
        "Grade Level": "grade",
        "IEP Status": "iep",
        "Race/Ethnicity": "raceethnicity",
        "Temporary Living Situation Status": "stls",
    }
    // info bubbles for the "Compare" legend, keyed by dataset label
    const legendTooltips = {
        School: getTooltip('legendschool'),
        Network: getTooltip('legendnetwork'),
        District: getTooltip('legenddistrict'),
    };
    const scopedId = (id: string) => `${blockId}-${id}`;
    const parentScope = '.' + scopedId('metric-block');
    const gradeField = ["Cohort Graduation 4-Year Rate", "Cohort Graduation 5-Year Rate"].includes(config.metricTitle) ? "Grade Entered CPS HS" : "Grade Level";


    // Logic: Use the CMS value if provided (and not 0), otherwise default to 100
    const xAxisMax = (config.xaxisvalue === 0 || !config.xaxisvalue) ? 100 : config.xaxisvalue;
    let totalMetricText = "Total Students: ";
    if (config.metricTitle.includes('Credit Attainment')) totalMetricText = "Total Credits: ";
    if (config.metricTitle.includes('Average Daily Attendance')) totalMetricText = "Total Cumulative Membership Days: ";

    // Putting this in becasue we need it for options right now, but it's a bit fragile.
    // if you have district on, you need to have school and network on. if you have network on, you need school on.
    // confirm this for trends AND snapshots
    const datasetDisplay = {
        school: true,
        network: true,
        district: true
    };
    // this (and other per chart stuff) should eventually be a setting, but don't want to mess up too much before a rewrite
    if ([
        'Star360',
        'Credit Attainment',
        'Daily Attendance',
        'Stabilization',
        'Growth in Attendance',
        'One-Year Grad',
        'One Year Grad',
        'Attendance growth rate',
    ].some(t => config.metricTitle.toLowerCase().includes(t.toLowerCase()))) {
        datasetDisplay.district = false;
    }
    if ([
        'Transition Rate', 'Cultivate Participation', 'Cultivate Response Rate'
    ].some(t => config.metricTitle.toLowerCase().includes(t.toLowerCase()))) {
        datasetDisplay.network = false;
        datasetDisplay.district = false;
    }

    // @TODO: over time we ended up adding a lot of per-chart config in this file that really shouldn't be here
    // when we refactor, most of it should be brought into the block configuration.

    const gradeOrder = ["PE", "PK", "PE/PK", "K", "KG", "1", "01", "2", "02", "3", "03", "4", "04", "5", "05", "6", "06", "7", "07", "8", "08", "9", "09", "10", "11", "12", "Not Listed"];

    const categoryGradeSort = (a, b) => gradeOrder.indexOf(a.CATEGORY_DESCRIPTION) - gradeOrder.indexOf(b.CATEGORY_DESCRIPTION);
    const yearSort = (a, b) => (trendLabels.indexOf(a.SCHOOL_YEAR) - trendLabels.indexOf(b.SCHOOL_YEAR));
    const categorySort = (a, b) => a.CATEGORYDESCRIPTION.localeCompare(b.CATEGORYDESCRIPTION);

    const tempShaper = (response) => {
        const data = [];
        for (let year of response.SchoolYears) {
            data.push(...year.Categories.map(category => ({
                ...upperizeStringify(category),
                CATEGORY_DESCRIPTION: category.CategoryDescription,
                SCHOOL_YEAR: year.SchoolYear,

                LESSTHAN10: category.Lessthan10 == "" ? "0" : category.Lessthan10,
                NODATA: category.NoData == "" ? "0" : category.NoData,

                NETWORK: response.Network
            })))
        }

        return data.filter(a => a.CATEGORY_DESCRIPTION != null && a.SCHOOL_YEAR != null)
            .sort(categorySort)
            .sort((a, b) => a.SCHOOL_YEAR.localeCompare(b.SCHOOL_YEAR));
    }

    let absenceData = tempShaper(fillMissingData(schoolResponse, StandardMetricSampleRow));
    let networkData = networkResponse ? tempShaper(fillMissingData(networkResponse, StandardMetricSampleRow)) : [];
    let districtData = districtResponse ? tempShaper(fillMissingData(districtResponse, StandardMetricSampleRow)) : [];

    if (config.metricTitle.includes('Credit Attainment')) {
        // supe rhacky place to round, but we don't have time for back and forth on a data refresh
        // the check for "null" is also kind of a disaster
        const roundNumerator = r => {
            if (r.NUMERATOR && r.NUMERATOR != 'null') {
                r.NUMERATOR = parseFloat(r.NUMERATOR).toFixed(1)
            }
            return r;
        }
        absenceData = absenceData.map(roundNumerator);
        networkData = networkData.map(roundNumerator);
        districtData = districtData.map(roundNumerator);
    }

    let schoolName = config.schoolName;
    let absence;
    let rawData = { "School": [], "District": [], "Network": [] };
    let currentYear = '2024-2025';
    let trendLabels = districtResponse ? districtResponse.SchoolYears.map(y => y.SchoolYear).sort() : [];
    if (config.metricTitle === 'Cultivate Participation' || config.metricTitle.toLowerCase() === 'cultivate response rate') {
        trendLabels = ['2024-2025'];
    }
    if (trendLabels.length > 0) currentYear = trendLabels[trendLabels.length - 1];
    let selectedCategory = "ALL";

    let snapshotLabels = {
        "ALL": getLabels("ALL"),
        "Race/Ethnicity": getLabels("Race/Ethnicity"),
        "Economic Disadvantage": getLabels("Economic Disadvantage"),
        "English Learner Status": getLabels("English Learner Status"),
        "Gender": getLabels("Gender"),
        [gradeField]: getGradeLabels(gradeField),
        "IEP Status": getLabels("IEP Status"),
        "Temporary Living Situation Status": getLabels("Temporary Living Situation Status")
    }

    let trendCharts = [
        [scopedId('chart-absence-all'), "All Students"],
        [scopedId('chart-economic-disadvantage1'), "Economic Disadvantage"],
        [scopedId('chart-economic-disadvantage2'), "Non-Economic Disadvantage"],
        [scopedId('chart-english-learner-status1'), "English Learner"],
        [scopedId('chart-english-learner-status2'), "Non-English Learner"],
        [scopedId('chart-gender1'), "Female"],
        [scopedId('chart-gender2'), "Male"],
        [scopedId('chart-gender3'), "Non-Binary"],
        [scopedId('chart-iep-status1'), "IEP"],
        [scopedId('chart-iep-status2'), "Non-IEP"],
        [scopedId('chart-race-ethnicity1'), "Asian"],
        [scopedId('chart-race-ethnicity2'), "Asian/Pacific Islander (Retired Category)"],
        [scopedId('chart-race-ethnicity3'), "Black/African American"],
        [scopedId('chart-race-ethnicity4'), "Hawaiian/Pacific Islander"],
        [scopedId('chart-race-ethnicity5'), "Latinx"],
        [scopedId('chart-race-ethnicity6'), "Multiracial"],
        [scopedId('chart-race-ethnicity7'), "Native American"],
        [scopedId('chart-race-ethnicity8'), "Not Listed"],
        [scopedId('chart-race-ethnicity9'), "White"],
        [scopedId('chart-temporary-living-situations'), "Non-Temporary Living Situation"],
        [scopedId('chart-temporary-living-situations2'), "Temporary Living Situation"]
    ]

    let trendLookup = {
        [scopedId("absence-all")]: "All Students",
        [scopedId("economic-disadvantage1")]: "Economic Disadvantage",
        [scopedId("economic-disadvantage2")]: "Non-Economic Disadvantage",
        [scopedId("english-learner-status1")]: "English Learner",
        [scopedId("english-learner-status2")]: "Non-English Learner",
        [scopedId("gender1")]: "Female",
        [scopedId("gender2")]: "Male",
        [scopedId("gender3")]: "Non-Binary",
        [scopedId("grade-levelPE/PK")]: "PE/PK",
        [scopedId("grade-levelPE")]: "PE",
        [scopedId("grade-levelPK")]: "PK",
        [scopedId("grade-levelK")]: "K",
        [scopedId("grade-levelKG")]: "KG",
        [scopedId("grade-level1")]: "1",
        [scopedId("grade-level2")]: "2",
        [scopedId("grade-level3")]: "3",
        [scopedId("grade-level4")]: "4",
        [scopedId("grade-level5")]: "5",
        [scopedId("grade-level6")]: "6",
        [scopedId("grade-level7")]: "7",
        [scopedId("grade-level8")]: "8",
        [scopedId("grade-level9")]: "9",
        // these are duped as a hack for now
        // when the chart stuff is fully refactored, this should just be self-contained to the chart construction
        [scopedId("grade-level01")]: "01",
        [scopedId("grade-level02")]: "02",
        [scopedId("grade-level03")]: "03",
        [scopedId("grade-level04")]: "04",
        [scopedId("grade-level05")]: "05",
        [scopedId("grade-level06")]: "06",
        [scopedId("grade-level07")]: "07",
        [scopedId("grade-level08")]: "08",
        [scopedId("grade-level09")]: "09",
        [scopedId("grade-level10")]: "10",
        [scopedId("grade-level11")]: "11",
        [scopedId("grade-level12")]: "12",
        [scopedId("iep-status1")]: "IEP",
        [scopedId("iep-status2")]: "Non-IEP",
        [scopedId("race-ethnicity1")]: "Asian",
        [scopedId("race-ethnicity2")]: "Asian/Pacific Islander (Retired Category)",
        [scopedId("race-ethnicity3")]: "Black/African American",
        [scopedId("race-ethnicity4")]: "Hawaiian/Pacific Islander",
        [scopedId("race-ethnicity5")]: "Latinx",
        [scopedId("race-ethnicity6")]: "Multiracial",
        [scopedId("race-ethnicity7")]: "Native American",
        [scopedId("race-ethnicity8")]: "Not Listed",
        [scopedId("race-ethnicity9")]: "White",
        [scopedId("temporary-living-situations")]: "Non-Temporary Living Situation",
        [scopedId("temporary-living-situations2")]: "Temporary Living Situation"
    }

    const snapshotTooltipConfig = {
        enabled: true,
        titleFont: tooltipTitleFont,
        callbacks: {
            title: function (tooltipItem) {
                return ''
            },
            label: function (tooltipItem) {
                let label = tooltipItem.dataset.label;
                let data = rawData[label];
                let match = data.find(value => value.CATEGORY_DESCRIPTION === tooltipItem.label && value.SCHOOL_YEAR === currentYear && value.CATEGORY === selectedCategory);
                let line1 = [`${label} ${config.metricTitle} Rate: ${tooltipItem.raw}%`];
                let line2 = `${config.metricTitle} Count: ${Number(match.NUMERATOR).toLocaleString()}`;
                let line3 = `${totalMetricText}${Number(match.DENOMINATOR).toLocaleString()}`;
                line1.push(line2, line3);
                return line1;
            }
        }
    };

    const trendTooltipConfig = {
        enabled: true,
        titleFont: tooltipTitleFont,
        callbacks: {
            title: function (tooltipItem) {
                return ''
            },
            label: function (tooltipItem) {
                let category = trendLookup[tooltipItem.chart.canvas.id];
                let type = null;
                if (tooltipItem.chart.canvas.id.includes("race")) {
                    type = "Race/Ethnicity"
                } else if (tooltipItem.chart.canvas.id.includes("gender")) {
                    type = "Gender"
                }
                let set = tooltipItem.dataset.label;
                let rawData;
                switch (set) {
                    case "School":
                        rawData = getTrendSchoolData(category, type);
                        break;
                    case "Network":
                        rawData = getTrendNetworkData(category, type);
                        break;
                    case "District":
                        rawData = getTrendDistrictData(category, type);
                }
                let label = tooltipItem.dataset.label;
                let match = rawData[tooltipItem.dataIndex];
                let line1 = [`${label} ${config.metricTitle} Rate: ${tooltipItem.raw}%`];
                let line2 = `School Year: ${tooltipItem.label}`;
                let line3 = `${config.metricTitle} Count: ${Number(match.NUMERATOR).toLocaleString()}`;
                let line4 = `${totalMetricText}${Number(match.DENOMINATOR).toLocaleString()}`;
                line1.push(line2, line3, line4);
                return line1;
            }
        }
    };


    const snapshotDatalabelConfig = {
        display: false,
        color: '#000000',
        font: datalabelFont,
    };

    const ethnicityDatalabelConfig = {
        display: false,
        color: '#000000',
        font: {
            size: 11,
            weight: 400,
        }
    };

    const trendDatalabelConfig = {
        display: false,
        color: '#000000',
        font: datalabelFont,
    };

    function getLabels(type) {
        return Array.from(new Set(absenceData.filter((value) =>
            value.CATEGORY === type)
            .map((item) => item.CATEGORY_DESCRIPTION))).sort()
    }

    function getGradeLabels(type) {
        return Array.from(new Set(absenceData.filter((item) =>
            item.CATEGORY === type)
            .sort(categoryGradeSort)
            .map((item) => item.CATEGORY_DESCRIPTION)));
    }

    // @TODO this should be a simple accessor with the new data structure
    function getNetworkData(year) {
        let values = rawData["Network"].filter((value) => value.SCHOOL_YEAR === year);
        return getData(values);
    }

    // @TODO this should be a simple accessor with the new data structure
    function getSnapshotRaw(label) {
        return rawData[label].filter((value) => value.SCHOOL_YEAR === currentYear);
    }

    function getData(values) {
        const ret = values.sort(selectedCategory == 'Grade Level' ? categoryGradeSort : categorySort).map(value => {
            if (value.RATE.toLowerCase() === "null") {
                if (value.LESSTHAN10 != "0") {
                    return value.LESSTHAN10;
                } else if (value.NODATA != "0") {
                    return value.NODATA;
                }
            } else {
                return value.RATE;
            }
        })
        return ret;
    }

    function getCenterLabels(type, year) {
        return rawData[type]
            .filter(v => v.SCHOOL_YEAR === year)
            .sort(selectedCategory == 'Grade Level' ? categoryGradeSort : categorySort)
            .map(value => {
                if (!value.RATE || value.RATE.toLowerCase() === "null") return null;
                const rate = parseFloat(value.RATE);
                if (isNaN(rate) || rate <= 0) return null;
                if (value.LESSTHAN10 != "0") return value.LESSTHAN10;
                if (value.NODATA != "0") return value.NODATA;
                return parseFloat(value.NUMERATOR).toLocaleString();
            });
    }

    // @TODO this should be a simple accessor with the new data structure
    function getSchoolData(year) {
        let values;
        if (year) {
            values = rawData["School"].filter((value) => value.SCHOOL_YEAR === year);
        } else {
            values = rawData["School"];
        }
        return getData(values);
    }

    // @TODO this should be a simple accessor with the new data structure
    function getDistrictData(year) {
        let values = rawData["District"].filter((value) => value.SCHOOL_YEAR === year);
        return getData(values);
    }


    // @TODO this will change witht he new data, not sure how though
    function getTrendSchoolData(category, type) {
        let values;
        if (type) {
            values = absenceData.filter((value) => value.CATEGORY_DESCRIPTION === category && value.CATEGORY === type);
        } else {
            values = absenceData.filter((value) => value.CATEGORY_DESCRIPTION === category);
        }
        return values.sort(yearSort);
    }

    // @TODO this will change witht he new data, not sure how though
    function getTrendNetworkData(category, type) {
        let values;
        if (type) {
            values = networkData.filter((value) => value.CATEGORY_DESCRIPTION === category && value.CATEGORY === type);
        } else {
            values = networkData.filter((value) => value.CATEGORY_DESCRIPTION === category);
        }
        return values.sort(yearSort);

    }

    // @TODO this will change witht he new data, not sure how though
    function getTrendDistrictData(category, type) {
        let values;
        if (type) {
            values = districtData.filter((value) => value.CATEGORY_DESCRIPTION === category && value.CATEGORY === type);
        } else {
            values = districtData.filter((value) => value.CATEGORY_DESCRIPTION === category);
        }
        return values
            .sort(yearSort);
    }

    function getTrendCenterLabels(rawRows) {
        return rawRows.map(d => {
            if (!d.RATE || d.RATE.toLowerCase() === "null") return null;
            const rate = parseFloat(d.RATE);
            if (isNaN(rate) || rate <= 0) return null;
            return Number(d.NUMERATOR).toLocaleString();
        });
    }

    function getTrendData(category, type) {
        const datasets = [];
        if (datasetDisplay.school) {
            const schoolRaw = getTrendSchoolData(category, type);
            datasets.push({
                label: 'School',
                data: getData(schoolRaw),
                backgroundColor: '#50C4D9',
                _centerLabels: getTrendCenterLabels(schoolRaw),
            });
        }
        if (datasetDisplay.network) {
            const networkRaw = getTrendNetworkData(category, type);
            datasets.push({
                label: 'Network',
                data: getData(networkRaw),
                backgroundColor: '#1D48C0',
                hidden: true,
                _centerLabels: getTrendCenterLabels(networkRaw),
                datalabels: {
                    color: 'white'
                }
            });
        }
        if (datasetDisplay.district) {
            const districtRaw = getTrendDistrictData(category, type);
            datasets.push({
                label: 'District',
                data: getData(districtRaw),
                backgroundColor: '#061A53',
                hidden: true,
                _centerLabels: getTrendCenterLabels(districtRaw),
                datalabels: {
                    color: 'white'
                }
            });
        }

        const fixedSets = datasets.map(set => {
            if (set.data.length == 0) {
                set.data = ['No Data', 'No Data', 'No Data']
            }
            return set;
        })

        const ret = {
            labels: trendLabels,
            datasets: fixedSets
        }
        return ret;
    }

    function getDropoutSnapshotData(label) {
        const datasets = [];
        if (datasetDisplay.school) datasets.push({
            label: 'School',
            data: getSchoolData(currentYear),
            backgroundColor: '#50C4D9',
            _centerLabels: getCenterLabels('School', currentYear),
        });
        if (datasetDisplay.network) datasets.push({
            label: 'Network',
            data: getNetworkData(currentYear),
            backgroundColor: '#1D48C0',
            _centerLabels: getCenterLabels('Network', currentYear),
            datalabels: {
                color: 'white'
            }
        });
        if (datasetDisplay.district) datasets.push({
            label: 'District',
            data: getDistrictData(currentYear),
            backgroundColor: '#061A53',
            _centerLabels: getCenterLabels('District', currentYear),
            datalabels: {
                color: 'white'
            }
        });
        return {
            labels: snapshotLabels[label],
            datasets: datasets
        }
    }

    function getAllSnapshotConfig(label) {
        const data = getDropoutSnapshotData(label);
        const hasSingleDataset = data.datasets.length < 2;
        const ret = {
            type: 'bar',
            plugins: chartsConfig.snapshots.plugins,
            data: getDropoutSnapshotData(label),
            options: {
                indexAxis: 'y',
                maintainAspectRatio: false,
                onResize: onResize,
                layout: {
                    padding: {
                        right: 50
                    }
                },
                scales: {
                    x: {
                        max: xAxisMax,
                        grid: {
                            display: true,
                            color: '#DDE0E7'
                        },
                        border: {
                            dash: [5, 5]
                        },
                        grace: 1.1,
                        title: {
                            display: true,
                            text: "% of Students",
                            color: "black"
                        },
                    },
                    y: {
                        grid: {
                            offset: false,
                            display: false
                        },
                        ticks: {
                            color: "black"
                        }
                    }
                },
                plugins: {
                    datalabels: snapshotDatalabelConfig,
                    tooltip: snapshotTooltipConfig,
                    legend: { display: false },
                    htmlLegend: hasSingleDataset? null :  {
                        containerID: `${blockId}-standard-template-snapshot`,
                        itemTooltips: legendTooltips
                    }
                }
            }
        }

        return ret;
    }

    function getCategoryTrendConfig(category, type = null) {
        const data = getTrendData(category, type);
        const hasSingleDataset = data.datasets.length < 2;
        return {
            type: 'bar' as const,
            //plugins: [ChartDataLabels, topLabelsTrends, htmlLegendPlugin],
            plugins: chartsConfig.trends.plugins,
            data: data,
            options: {
                onResize: onResize,
                maintainAspectRatio: false,
                indexAxis: 'y' as const,
                layout: {
                    padding: {
                        right: 50
                    }
                },
                scales: {
                    x: {
                        max: xAxisMax,
                        grid: {
                            display: true,
                            color: '#DDE0E7'
                        },
                        border: {
                            dash: [5, 5]
                        },
                        grace: 1,
                        title: {
                            display: true,
                            text: "% of Students"
                        },
                        ticks: {
                            display: true,
                        }
                    },
                    y: {
                        grid: {
                            offset: false,
                            display: false
                        },
                    }
                },
                plugins: {
                    datalabels: trendDatalabelConfig,
                    tooltip: trendTooltipConfig,
                    legend: {
                        display: false, // This is fine, but ensure labels are still configured
                        labels: {
                            // Ensure this isn't overridden to null
                        }
                    },
                    htmlLegend: hasSingleDataset ? null : { // if there's only one data set, don't show the toggle legend
                        containerID: generateChartId(category, `${blockId}-trend`),
                        itemTooltips: legendTooltips
                    }
                }
            }
        }
    }

    function getEthnicitySnapshotConfig() {
        console.log('getEthnicitySnapshotConfig')
        return {
            type: 'bar',
            plugins: chartsConfig.snapshots.plugins,
            data: getDropoutSnapshotData('Race/Ethnicity'),
            options: {
                indexAxis: 'y',
                maintainAspectRatio: false,
                onResize: onResize,
                scales: {
                    x: {
                        max: 100,
                        grid: {
                            display: true,
                            color: '#DDE0E7'
                        },
                        border: {
                            dash: [5, 5]
                        },
                        title: {
                            display: true,
                            text: "% of Students",
                            color: "black"
                        }
                    },
                    y: {
                        grid: {
                            offset: false,
                            display: false
                        },

                        ticks: {
                            color: "black",
                            display: true,
                            callback: function (label, value) {
                                let name = snapshotLabels["Race/Ethnicity"][value];
                                if (name.includes("Retired Category")) {
                                    return ['Asian/', 'Pacific Islander*'];
                                } else if (name.indexOf("/") > 1) {
                                    let brokenLabel = this.getLabelForValue(value).split("/")
                                    brokenLabel[0] += "/";
                                    return brokenLabel;
                                } else {
                                    return name;
                                }
                            },
                        }
                    }
                },
                plugins: {
                    datalabels: ethnicityDatalabelConfig,
                    tooltip: snapshotTooltipConfig,
                    legend: {
                        display: false, // This is fine, but ensure labels are still configured
                        labels: {
                            // Ensure this isn't overridden to null
                        }
                    },
                    htmlLegend: {
                        containerID: `${blockId}-snapshot-Ethnicity`,
                        itemTooltips: legendTooltips
                    },
                }
            }
        }
    }

    function changeDropoutDemographic(event) {
        let selected = event.target.value;
        selectedCategory = selected;
        // trends are all built ahead of time, snapshots share the same DOM elements and change the text
        const $snapshotContainer = $(parentScope + ' .snapshot-container,' + parentScope + ' .snapshot-table-container');
        $snapshotContainer.find('.category-title').text(selected);
        const $snapshotTooltip = $snapshotContainer.find(".category-tooltip");
        const tipContent = getCategoryMappedTooltip(selected);
        $snapshotTooltip.attr('title', tipContent);
        $snapshotTooltip.attr('data-tooltip-content', tipContent);
        infoBubble($snapshotTooltip[0])
        setTrendChart();

        absence.destroy();
        // @TODO mike rawData assignment
        rawData["School"] = absenceData.filter((value) => value.CATEGORY === selected).sort(yearSort);
        rawData["Network"] = networkData.filter((value) => value.CATEGORY === selected &&
            snapshotLabels[selectedCategory].includes(value.CATEGORY_DESCRIPTION)).sort(yearSort);
        rawData["District"] = districtData.filter((value) => value.CATEGORY === selected &&
            snapshotLabels[selectedCategory].includes(value.CATEGORY_DESCRIPTION)).sort(yearSort);

        let $retired = $(`#${scopedId('retired')}`);
        setSnapshotHeight();
        const snapshotTrendsLagends = document.getElementById(`${blockId}-standard-template-snapshot`);
        const snapshotLagends = document.getElementById(`${blockId}-snapshot-Ethnicity`);

        switch (selected) {
            case "Race/Ethnicity":
                if (snapshotTrendsLagends) {
                    snapshotTrendsLagends.style.display = 'none'
                }
                if (snapshotLagends) {
                    snapshotLagends.style.display = 'flex'
                }
                absence = new Chart(
                    // @ts-ignore
                    document.getElementById(scopedId('absence')),
                    getEthnicitySnapshotConfig()
                );
                console.log("Race/Ethnicity", absence)
                setSnapshotHeight();
                $retired.removeClass("hidden");
                break;
            default:
                if (snapshotLagends) {
                    snapshotLagends.style.display = 'none'
                }
                if (snapshotTrendsLagends) {
                    snapshotTrendsLagends.style.display = 'flex'
                }
                absence = new Chart(
                    // @ts-ignore
                    document.getElementById(scopedId('absence')),
                    getAllSnapshotConfig(selected)
                );
                $retired.addClass("hidden");
                break;
        }

        createDropoutTable();
    }

    function createDropoutTable() {
        let labels = snapshotLabels[selectedCategory];
        let previousChart = document.getElementById(scopedId('absenceTable'));
        if (previousChart) {
            previousChart.remove();
        }
        const absenceSnapshotChartBox = document.getElementById(scopedId('absence-chart'));
        const absenceSnapshotTableDiv = document.createElement('div');
        absenceSnapshotTableDiv.setAttribute('id', scopedId('absenceTable'));
        const table = document.createElement('table');
        table.classList.add('chart-table');
        table.setAttribute('id', scopedId('absence-table'));


        const thead = table.createTHead();
        thead.insertRow(0);

        for (let i = 0; i < labels.length; i++) {
            thead.rows[0].insertCell(i).outerHTML = "<th>" + labels[i] + "</th>";
        }

        thead.rows[0].insertCell(0).classList.add('light-cyan');
        const tbody = table.createTBody();
        let rowCount = 0;
        tbody.insertRow(rowCount);
        rowCount++;
        let sortMethod = categorySort;
        if (selectedCategory == 'Grade Level') {
            sortMethod = categoryGradeSort;
        }
        let raw = getSnapshotRaw("School").sort(sortMethod);
        raw.forEach((value, i) => {
            let text = raw[i].RATE;
            if (text === "null") {
                if (raw[i].LESSTHAN10 != "0") {
                    tbody.rows[0].insertCell(i).innerText = raw[i].LESSTHAN10;
                } else if (raw[i].NODATA != "0") {
                    tbody.rows[0].insertCell(i).innerText = raw[i].NODATA;
                } else {
                    tbody.rows[0].insertCell(i).innerText = `N/A`;
                }
            } else {
                tbody.rows[0].insertCell(i).innerText = `${text.toLocaleString()}%\n${Number(raw[i].NUMERATOR).toLocaleString()}`;
            }
        });
        tbody.rows[0].insertCell(0).outerHTML = "<th scope='row'>School</th>";

        if (datasetDisplay.network) {
            tbody.insertRow(rowCount);
            raw = getSnapshotRaw("Network").sort(sortMethod);

            raw.forEach((value, i) => {
                let text = raw[i].RATE;
                if (text === "null") {
                    if (raw[i].LESSTHAN10 != "0") {
                        tbody.rows[rowCount].insertCell(i).innerText = raw[i].LESSTHAN10;
                    } else if (raw[i].NODATA != "0") {
                        tbody.rows[rowCount].insertCell(i).innerText = raw[i].NODATA;
                    } else {
                        tbody.rows[rowCount].insertCell(i).innerText = `N/A`;
                    }
                } else {
                    tbody.rows[rowCount].insertCell(i).innerText = `${text.toLocaleString()}%\n${Number(raw[i].NUMERATOR).toLocaleString()}`;
                }

            })
            tbody.rows[rowCount].insertCell(0).outerHTML = "<th scope='row'>Network</th>";
            rowCount++;
        }
        if (datasetDisplay.district) {
            tbody.insertRow(rowCount);
            raw = getSnapshotRaw("District").sort(sortMethod);
            raw.forEach((value, i) => {
                let text = raw[i].RATE;

                if (text === "null") {
                    if (raw[i].LESSTHAN10 != "0") {
                        tbody.rows[rowCount].insertCell(i).innerText = raw[i].LESSTHAN10;
                    } else if (raw[i].NODATA != "0") {
                        tbody.rows[rowCount].insertCell(i).innerText = raw[i].NODATA;
                    } else {
                        tbody.rows[rowCount].insertCell(i).innerText = `N/A`;
                    }
                } else {
                    tbody.rows[rowCount].insertCell(i).innerText = `${text.toLocaleString()}%\n${Number(raw[i].NUMERATOR).toLocaleString()}`;
                }

            });
            tbody.rows[rowCount].insertCell(0).outerHTML = "<th scope='row'>District</th>";
            rowCount++;
        }

        //append
        absenceSnapshotChartBox.appendChild(absenceSnapshotTableDiv);
        absenceSnapshotTableDiv.appendChild(table);
    }

    function createTrendTables() {
        getGradeTables();

        trendCharts.forEach((chart) => {
            let type = null;
            if (chart[0].includes("race")) {
                type = "Race/Ethnicity"
            } else if (chart[0].includes("gender")) {
                type = "Gender"
            } else if (chart[0].includes("grade")) {
                type = "Grade Level"
            }
            const categoryChartBox = document.getElementById(chart[0]);
            let categoryChartTableDiv = document.createElement('div');
            let categoryTable = document.createElement('table');
            categoryTable.classList.add('chart-table');
            let categoryThead = categoryTable.createTHead();
            categoryThead.insertRow(0);
            categoryThead.rows[0].insertCell(0).classList.add('light-cyan');
            trendLabels.forEach((label, index) => {
                categoryThead.rows[0].insertCell(index + 1).outerHTML = `<th>${label}</th>`;
            })
            const categoryTableBody = categoryTable.createTBody();
            let rowCount = 0;
            categoryTableBody.insertRow(rowCount);
            rowCount++;

            let trendData = getTrendSchoolData(chart[1], type);
            trendData.forEach((value, i) => {
                let rate = value.RATE;
                if (value.RATE === "null") {
                    if (value.LESSTHAN10 != "0") {
                        categoryTableBody.rows[0].insertCell(i).innerText = value.LESSTHAN10
                    } else if (value.NODATA != "0") {
                        categoryTableBody.rows[0].insertCell(i).innerText = value.NODATA;
                    } else {
                        categoryTableBody.rows[0].insertCell(i).innerText = 'N/A';
                    }
                } else {
                    categoryTableBody.rows[0].insertCell(i).innerText = `${rate.toLocaleString()}%\n${Number(value.NUMERATOR).toLocaleString()}`
                }
            })
            categoryTableBody.rows[0].insertCell(0).outerHTML = "<th scope='row'>School</th>";

            if (datasetDisplay.network) {
                categoryTableBody.insertRow(rowCount);
                let networkData = getTrendNetworkData(chart[1], type);
                networkData.forEach((value, i) => {
                    let rate = value.RATE;
                    if (value.RATE === "null") {
                        if (value.LESSTHAN10 != "0") {
                            categoryTableBody.rows[rowCount].insertCell(i).innerText = value.LESSTHAN10
                        } else if (value.NODATA != "0") {
                            categoryTableBody.rows[rowCount].insertCell(i).innerText = value.NODATA;
                        } else {
                            categoryTableBody.rows[rowCount].insertCell(i).innerText = 'N/A';
                        }
                    } else {
                        categoryTableBody.rows[rowCount].insertCell(i).innerText = `${rate.toLocaleString()}%\n${Number(value.NUMERATOR).toLocaleString()}`
                    }
                })
                categoryTableBody.rows[rowCount].insertCell(0).outerHTML = "<th scope='row'>Network</th>";
                rowCount++;
            }

            if (datasetDisplay.district) {
                categoryTableBody.insertRow(rowCount);
                let districtData = getTrendDistrictData(chart[1], type);
                districtData.forEach((value, i) => {
                    let rate = value.RATE;
                    if (value.RATE === "null") {
                        if (value.LESSTHAN10 != "0") {
                            categoryTableBody.rows[rowCount].insertCell(i).innerText = value.LESSTHAN10
                        } else if (value.NODATA != "0") {
                            categoryTableBody.rows[rowCount].insertCell(i).innerText = value.NODATA;
                        } else {
                            categoryTableBody.rows[rowCount].insertCell(i).innerText = 'N/A';
                        }
                    } else {
                        categoryTableBody.rows[rowCount].insertCell(i).innerText = `${rate.toLocaleString()}%\n${Number(value.NUMERATOR).toLocaleString()}`
                    }
                })
                categoryTableBody.rows[rowCount].insertCell(0).outerHTML = "<th scope='row'>District</th>";
                rowCount;
            }

            categoryChartBox.appendChild(categoryChartTableDiv);
            categoryChartTableDiv.appendChild(categoryTable);
        })
    }

    function createSnapshot() {
        rawData["School"] = absenceData.filter((value) => value.CATEGORY === selectedCategory).sort(yearSort);
        rawData["Network"] = networkData.filter((value) => value.CATEGORY === selectedCategory &&
            snapshotLabels[selectedCategory].includes(value.CATEGORY_DESCRIPTION)).sort(yearSort);
        rawData["District"] = districtData.filter((value) => value.CATEGORY === selectedCategory &&
            snapshotLabels[selectedCategory].includes(value.CATEGORY_DESCRIPTION)).sort(yearSort);
        absence = new Chart(
            // @ts-ignore
            document.getElementById(scopedId('absence')),
            getAllSnapshotConfig(selectedCategory)
        );

        createDropoutTable();
    }

    async function buildTrends() {
        createTrends();
        createTrendTables();
    }

    function getGradeCharts() {
        let gradesServed = snapshotLabels[gradeField];
        let parent = document.getElementById(scopedId('absence-grade-charts'));

        gradesServed.forEach((grade) => {
            //const gradeTip = getTooltip(gradeField);
            let container = document.createElement("div");
            container.classList.add("trend-container");

            // @TODO mike this is tooltip stuff that needs to be pulled out
            //const tipContent = bubbleConfigs[mappedBubbleConfigFieldKeys[`${gradeField} - ${grade}`]]?.DescriptionHtmlString ?? (notUseDefaultTooltip ? null : gradeTip);

            const tipContent = getTooltip('grade');
            container.innerHTML = tipContent ?
                `<p style="text-align: center;"><strong><span class="school-name">${schoolName}</span> - ${config.metricTitle} Rate - Grade ${grade} <span data-toggle="school-profile-tooltip"
                    class="school-info-bubble fa fa-info-circle" data-html="true" data-tooltip-content="${tipContent}" aria-label="More Info: Click here"></span></strong>
                </p>` :
                `<p style="text-align: center;"><strong><span class="school-name">${schoolName}</span> - ${config.metricTitle} Rate - Grade ${grade} </strong>
                </p>`;

            let canvasDiv = document.createElement('canvas');
            canvasDiv.setAttribute('id', scopedId(`grade-level${grade}`));
            container.appendChild(canvasDiv);
            parent.appendChild(container);
            try {
                new Chart(
                    // @ts-ignore
                    document.getElementById(scopedId(`grade-level${grade}`)),

                    getCategoryTrendConfig(grade, "Grade Level")
                );
            } catch (e) {
                console.error('failed to create chart', e)
            }
        });
        const tipContent = getTooltip('grade');
        parent.querySelectorAll<HTMLElement>('[data-toggle="school-profile-tooltip"]').forEach((el) => {

            infoBubble(el);
        });
    }

    function getGradeTables() {
        //const gradeTip = getTooltip(gradeField);
        let gradesServed = snapshotLabels[gradeField];
        let parent = document.getElementById(scopedId('absence-grade-tables'));
        gradesServed.forEach((grade) => {
            let container = document.createElement("div");
            container.classList.add("table-scrolling-wrapper");

            //const tipContent = bubbleConfigs[mappedBubbleConfigFieldKeys[`${gradeField} - ${grade}`]]?.DescriptionHtmlString ?? (notUseDefaultTooltip ? null : gradeTip);
            const tipContent = getTooltip('grade');
            container.innerHTML = tipContent ?
                `<p style="text-align: center;"><strong><span class="school-name">${schoolName}</span> - ${config.metricTitle} Rate - Grade ${grade} <span data-toggle="school-profile-tooltip"
                                                                                 class="school-info-bubble fa fa-info-circle"
                                                                                 data-html="true"
                                                                                 data-tooltip-content="${tipContent}" aria-label="More Info: Click here"></span></strong>
                        </p>
                        <div id="${scopedId('chart-grade-level')}${grade}">&nbsp;</div>` :
                `<p style="text-align: center;"><strong><span class="school-name">${schoolName}</span> - ${config.metricTitle} Rate - Grade ${grade} </strong>
                        </p>
                        <div id="${scopedId('chart-grade-level')}${grade}">&nbsp;</div>`;

            parent.appendChild(container)

            container.querySelectorAll<HTMLElement>('[data-toggle="school-profile-tooltip"]').forEach((el) => {
                infoBubble(el);
            });
        })
    }

    function createTrends() {
        return new Promise((resolve) => {
            // @TODO mike check to make sure this actually waits on resolution, looks like it might not?
            let trends = [
                {
                    var: 'absenceTrendDisadvantage1',
                    id: scopedId('economic-disadvantage1'),
                    name: "Economic Disadvantage"
                },
                {
                    var: 'absenceTrendDisadvantage2',
                    id: scopedId('economic-disadvantage2'),
                    name: 'Non-Economic Disadvantage'
                },
                { var: 'absenceTrendEls1', id: scopedId('english-learner-status1'), name: 'English Learner' },
                { var: 'absenceTrendEls2', id: scopedId('english-learner-status2'), name: 'Non-English Learner' },
                { var: 'absenceTrendGender1', id: scopedId('gender1'), name: 'Female' },
                { var: 'absenceTrendGender2', id: scopedId('gender2'), name: 'Male' },
                { var: 'absenceTrendGender3', id: scopedId('gender3'), name: 'Non-Binary' },
                // @TODO this is where `getGradeCharts()` was, if it matters

                { var: 'iepStatus', id: scopedId('iep-status1'), name: 'IEP' },
                { var: 'iepStatus2', id: scopedId('iep-status2'), name: 'Non-IEP' },

                { var: 'absenceTrendRace1', id: scopedId('race-ethnicity1'), name: 'Asian' },
                {
                    var: 'absenceTrendRace2',
                    id: scopedId('race-ethnicity2'),
                    name: 'Asian/Pacific Islander (Retired Category)'
                },
                { var: 'absenceTrendRace3', id: scopedId('race-ethnicity3'), name: 'Black/African American' },
                { var: 'absenceTrendRace4', id: scopedId('race-ethnicity4'), name: 'Hawaiian/Pacific Islander' },
                { var: 'absenceTrendRace5', id: scopedId('race-ethnicity5'), name: 'Latinx' },
                { var: 'absenceTrendRace6', id: scopedId('race-ethnicity6'), name: 'Multiracial' },
                { var: 'absenceTrendRace7', id: scopedId('race-ethnicity7'), name: 'Native American' },
                // `type` needs to be added if the `name` can be in more than one category in the data
                { var: 'absenceTrendRace8', id: scopedId('race-ethnicity8'), name: 'Not Listed', type: 'Race/Ethnicity' },
                { var: 'absenceTrendRace9', id: scopedId('race-ethnicity9'), name: 'White' },

                {
                    var: 'absenceTrendTLS',
                    id: scopedId('temporary-living-situations'),
                    name: 'Non-Temporary Living Situation'
                },
                {
                    var: 'absenceTrendTLS2',
                    id: scopedId('temporary-living-situations2'),
                    name: 'Temporary Living Situation'
                },
            ];
            const absenceTrendAll = new Chart(
                document.getElementById(scopedId('absence-all')) as HTMLCanvasElement,
                getCategoryTrendConfig("All Students")
            );
            for (let trend of trends) {
                new Chart(
                    document.getElementById(trend.id) as HTMLCanvasElement,
                    getCategoryTrendConfig(trend.name, trend.type ?? null))
            }

            getGradeCharts();

            resolve(true);
        })
    }

    function showSnapshot() {
        // check the view dropdown and make sure it's selected on switch
        // bit hacky, but we're refactoring. using aria-posinset just because it's a little more resiliant for now
        const $currentViewType = $(parentScope + ' .absence-trends .tabs-list-container button[aria-selected=true]');
        const ariaPos = $currentViewType.attr('aria-posinset');
        const $newView = $(parentScope + ` .absence-snapshot .tabs-list-container button[aria-posinset=${ariaPos}]`);
        $newView.click();


        $(`#${scopedId('show-trends')}`).removeClass("active");
        $(`#${scopedId('show-snapshot')}`).addClass("active");
        $(parentScope + " .absence-trends").hide();
        $(parentScope + " .absence-snapshot").show();
    }

    function showTrends() {
        // check the view dropdown and make sure it's selected on switch
        // bit hacky, but we're refactoring. using aria-posinset just because it's a little more resiliant for now
        const $currentViewType = $(parentScope + ' .absence-snapshot .tabs-list-container button[aria-selected=true]');
        const ariaPos = $currentViewType.attr('aria-posinset');
        const $newView = $(parentScope + ` .absence-trends .tabs-list-container button[aria-posinset=${ariaPos}]`);
        $newView.click();

        $(`#${scopedId('show-snapshot')}`).removeClass("active");
        $(`#${scopedId('show-trends')}`).addClass("active");

        $(parentScope + " .absence-snapshot").hide();
        $(parentScope + " .absence-trends").show();
    }

    function hideTrendCharts() {
        $(parentScope).find("div.absence-all, div.absence-economic,div.absence-english,div.absence-gender,div.absence-grade,div.absence-iep,div.absence-race,div.absence-temporary").hide();
    }

    function setTrendsHeight() {
        if (trendLabels.length < 3) {
            const scopeKey = scopedId(`metric-block`);
            const newHeight = (115 + (85 * Math.max(trendLabels.length, 1))).toFixed(0);
            const referenceNode = document.querySelector(`[class~="${scopeKey}"]`);
            if (referenceNode) {
                const styleNode = document.createElement('style');

                styleNode.innerHTML = `
            [class~="${scopeKey}"] .trend-container canvas{ 
                max-height: ${newHeight}px !important; 
            }
        `;
                referenceNode.prepend(styleNode);
            }
        }
    }

    function setTrendChart() {
        hideTrendCharts();
        switch (selectedCategory) {
            case "ALL":
                $(parentScope).find("div.absence-all").show();
                break;
            case "Race/Ethnicity":
                $(parentScope).find("div.absence-race").show();
                break;
            case "Economic Disadvantage":
                $(parentScope).find("div.absence-economic").show();
                break;
            case "English Learner Status":
                $(parentScope).find("div.absence-english").show();
                break;
            case "Gender":
                $(parentScope).find("div.absence-gender").show();
                break;
            case gradeField:
                $(parentScope).find("div.absence-grade").show();
                break;
            case "IEP Status":
                $(parentScope).find("div.absence-iep").show();
                break;
            case "Temporary Living Situation Status":
                $(parentScope).find("div.absence-temporary").show();
                break;
        }
    }

    function setSnapshotHeight() {
        let length = snapshotLabels[selectedCategory].length;
        let height = (115 + (85 * Math.max(length, 1))).toFixed(0);
        let snapshotHeight = document.getElementById(scopedId("snapshot-height"));
        let canvas = snapshotHeight.querySelector('canvas');
        snapshotHeight.style.height = height.toString() + "px";
        if (canvas) {
            canvas.style.height = (115 + (85 * Math.max(length, 1))).toFixed(0).toString() + "px";
            canvas.style.maxHeight = (115 + (85 * Math.max(length, 1))).toFixed(0).toString() + "px"
        }
        if (length < 3) {
            snapshotHeight.style.minHeight = height.toString() + "px";
        }
    }

    $(parentScope + ' .category-title').text("ALL");

    snapshotLabels[gradeField].forEach((label) => {
        trendCharts.push([scopedId(`chart-grade-level${label}`), label])
    })

    $(parentScope + " .absence-snapshot").hide().removeClass('hidden');

    let absenceSelect = document.getElementById(scopedId("absence-demographics"));
    absenceSelect.addEventListener("change", changeDropoutDemographic);

    createSnapshot();
    setTrendChart();
    setSnapshotHeight();
    setTrendsHeight()

    $(`#${scopedId('show-trends')}`).on("click", showTrends);
    $(`#${scopedId('show-snapshot')}`).on("click", showSnapshot);

    $(document).ready(function () {
        buildTrends()
    });
});
